import { useMemo } from 'react';

export const INVESTOR_BLUE = 'rgb(37 99 235)';
export const FLIPPEX_PINK = 'rgb(215 57 134)';

export const num = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

export const money = (value, currency = 'EUR', decimals = 0) => {
  const parsed = num(value);
  if (parsed === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parsed);
};

export const compactMoney = (value, currency = 'EUR') => {
  const parsed = num(value);
  if (parsed === null) return '—';
  if (Math.abs(parsed) >= 1000000) return `${money(parsed / 1000000, currency, 1)}M`;
  if (Math.abs(parsed) >= 1000) return `${money(Math.round(parsed / 1000), currency)}K`;
  return money(parsed, currency);
};

export const pct = (value, decimals = 1) => {
  const parsed = num(value);
  if (parsed === null) return '—';
  return `${parsed.toFixed(decimals)}%`;
};

export const monthsLabel = (value) => {
  const parsed = num(value);
  if (parsed === null) return '—';
  return `${parsed} ${parsed === 1 ? 'month' : 'months'}`;
};

/**
 * Single source of truth for the investment economics rendered at every breakpoint.
 * All values are derived from the backend instrument payload - nothing hard-coded.
 */
export const useInstrumentEconomics = (instrument) =>
  useMemo(() => {
    if (!instrument) return null;
    const nav = instrument.nav_ownership || {};

    const sharePrice = num(instrument.unit_price);
    const totalShares = num(instrument.total_authorized_units) || 0;
    const availableShares = num(instrument.units_available) ?? 0;
    const fundedShares = Math.max(totalShares - availableShares, 0);
    const totalRaise = num(nav.total_raise) ?? (sharePrice && totalShares ? sharePrice * totalShares : null);
    const fundedPercentage = totalShares > 0 ? (fundedShares / totalShares) * 100 : 0;
    const remainingCapital = sharePrice !== null ? availableShares * sharePrice : null;

    // Never show two near-identical return figures at the same hierarchy level.
    const annual = num(instrument.estimated_annual_return);
    const project = num(instrument.estimated_project_return);
    const bothMeaningful = annual !== null && project !== null && Math.abs(annual - project) >= 0.5;
    const primaryReturn = project ?? annual;

    const startingNav = num(nav.starting_nav);
    const currentNav = num(nav.current_nav) ?? startingNav;
    const appreciation = startingNav && currentNav ? currentNav - startingNav : 0;
    const appreciationPct = startingNav ? (appreciation / startingNav) * 100 : 0;

    return {
      sharePrice,
      totalShares,
      availableShares,
      fundedShares,
      totalRaise,
      fundedPercentage,
      remainingCapital,
      primaryReturn,
      annualReturn: bothMeaningful ? annual : null,
      yieldPct: num(instrument.estimated_monthly_noi_pct) !== null ? num(instrument.estimated_monthly_noi_pct) * 12 : null,
      appreciationReturnPct: num(instrument.estimated_total_appreciation_pct),
      minHold: num(instrument.min_hold_period_months),
      targetHold: num(instrument.target_hold_months) ?? num(instrument.max_hold_period_months),
      startingNav,
      valuationDate: nav.current_nav_date || null,
      currentNav,
      appreciation,
      appreciationPct,
      investorPercent: num(nav.investor_ownership_percent),
      flippexPercent: num(nav.flippex_ownership_percent),
      investorValue: num(nav.investor_stake_value) ?? num(nav.total_raise),
      flippexValue: num(nav.flippex_stake_value),
      hasOwnership: num(nav.investor_ownership_percent) !== null && (num(nav.current_nav) ?? startingNav) !== null,
    };
  }, [instrument]);
